// 在 GlbModel.vue 中添加 IndexedDB 缓存管理

import { message } from 'ant-design-vue'

// IndexedDB 配置
const DB_NAME = 'ModelCacheDB'
const DB_VERSION = 1
const STORE_NAME = 'models'

/**
 * 初始化 IndexedDB
 */
export function initIndexedDB () {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)

    request.onerror = (event) => {
      console.error('打开 IndexedDB 失败:', (event.target as IDBOpenDBRequest).error)
      reject((event.target as IDBOpenDBRequest).error)
    }

    request.onsuccess = (event) => {
      const db = (event.target as IDBOpenDBRequest).result
      resolve(db)
    }

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result

      // 创建存储对象（如果不存在）
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'key' })
        store.createIndex('timestamp', 'timestamp', { unique: false })
        console.log('IndexedDB 存储已创建')
      }
    }
  })
}

/**
 * 保存模型到 IndexedDB
 */
export async function saveModelToIndexedDB (key: string, zipBlob: Blob, modelInfo: any) {
  try {
    const db: any = await initIndexedDB()

    const transaction = db.transaction([STORE_NAME], 'readwrite')
    const store = transaction.objectStore(STORE_NAME)

    const modelData = {
      key: key,
      blob: zipBlob,
      timestamp: new Date().getTime(),
      modelName: modelInfo.modelName || '',
      modelUrl: modelInfo.modelUrl || '',
      fileType: modelInfo.fileType || '',
      size: zipBlob.size
    }

    return new Promise((resolve, reject) => {
      const request = store.put(modelData)

      request.onsuccess = () => {
        console.log('模型已保存到 IndexedDB:', key, '大小:', formatFileSize(zipBlob.size))
        resolve(true)
      }

      request.onerror = (event: Event) => {
        console.error('保存到 IndexedDB 失败:', (event.target as IDBRequest).error)
        reject((event.target as IDBRequest).error)
      }

      transaction.oncomplete = () => {
        db.close()
      }
    })
  } catch (error) {
    console.error('保存模型到 IndexedDB 失败:', error)
    throw error
  }
}

// 辅助函数：格式化文件大小
function formatFileSize (bytes: number) {
  if (!bytes) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
}

/**
 * 从 IndexedDB 获取模型
 */
export async function getModelFromIndexedDB (key: string) {
  try {
    const db: any = await initIndexedDB()

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readonly')
      const store = transaction.objectStore(STORE_NAME)

      const request = store.get(key)

      request.onsuccess = (event: Event) => {
        const result = (event.target as IDBRequest).result
        db.close()

        if (result && result.blob) {
          console.log('从 IndexedDB 获取模型:', key, '大小:', formatFileSize(result.blob.size))
          resolve(result.blob)
        } else {
          resolve(null)
        }
      }

      request.onerror = (event: Event) => {
        db.close()
        console.error('从 IndexedDB 获取模型失败:', (event.target as IDBRequest).error)
        reject((event.target as IDBRequest).error)
      }
    })
  } catch (error) {
    console.error('访问 IndexedDB 失败:', error)
    throw error
  }
}

/**
 * 删除 IndexedDB 中的模型
 */
export async function deleteModelFromIndexedDB (key: string) {
  try {
    const db: any = await initIndexedDB()

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite')
      const store = transaction.objectStore(STORE_NAME)

      const request = store.delete(key)

      request.onsuccess = () => {
        console.log('已从 IndexedDB 删除模型:', key)
        resolve(true)
      }

      request.onerror = (event: Event) => {
        console.error('从 IndexedDB 删除模型失败:', (event.target as IDBRequest).error)
        reject((event.target as IDBRequest).error)
      }

      transaction.oncomplete = () => {
        db.close()
      }
    })
  } catch (error) {
    console.error('删除模型失败:', error)
    throw error
  }
}

/**
 * 清理 IndexedDB 缓存（保留最新的 N 个）
 */
export async function cleanupIndexedDBCache (maxItems = 5) {
  try {
    const db: any = await initIndexedDB()

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite')
      const store = transaction.objectStore(STORE_NAME)
      const timestampIndex = store.index('timestamp')

      // 获取所有记录
      const getAllRequest = store.getAll()

      getAllRequest.onsuccess = (event: Event) => {
        const items = (event.target as IDBRequest).result

        if (items.length <= maxItems) {
          db.close()
          resolve(false) // 无需清理
          return
        }

        // 按时间戳排序
        items.sort((a: any, b: any) => a.timestamp - b.timestamp)

        // 删除最旧的记录
        const itemsToDelete = items.slice(0, items.length - maxItems)
        let deletedCount = 0

        if (itemsToDelete.length === 0) {
          db.close()
          resolve(false)
          return
        }

        itemsToDelete.forEach((item: any) => {
          const deleteRequest = store.delete(item.key)
          deleteRequest.onsuccess = () => {
            deletedCount++
            console.log('清理缓存:', item.key)

            if (deletedCount === itemsToDelete.length) {
              db.close()
              console.log(`清理完成，删除了 ${deletedCount} 个旧缓存`)
              resolve(true)
            }
          }

          deleteRequest.onerror = (e: Event) => {
            console.error('删除缓存失败:', (e.target as IDBRequest).error)
          }
        })
      }

      getAllRequest.onerror = (event: Event) => {
        db.close()
        reject((event.target as IDBRequest).error)
      }
    })
  } catch (error) {
    console.error('清理缓存失败:', error)
    throw error
  }
}

/**
 * 获取 IndexedDB 缓存统计
 */
export async function getIndexedDBCacheStats () {
  try {
    const db: any = await initIndexedDB()

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readonly')
      const store = transaction.objectStore(STORE_NAME)

      const countRequest = store.count()
      const getAllRequest = store.getAll()

      let totalCount = 0
      let totalSize = 0
      let items: any[] = []

      countRequest.onsuccess = (event: Event) => {
        totalCount = (event.target as IDBRequest).result
      }

      getAllRequest.onsuccess = (event: Event) => {
        items = (event.target as IDBRequest).result
        totalSize = items.reduce((sum, item) => sum + (item.size || 0), 0)

        db.close()

        resolve({
          count: totalCount,
          totalSize: totalSize,
          formattedTotalSize: formatFileSize(totalSize),
          items: items.map(item => ({
            key: item.key,
            modelName: item.modelName || '未知',
            size: item.size || 0,
            formattedSize: formatFileSize(item.size || 0),
            timestamp: new Date(item.timestamp).toLocaleString(),
            fileType: item.fileType || '未知'
          }))
        })
      }

      getAllRequest.onerror = (event: Event) => {
        db.close()
        reject((event.target as IDBRequest).error)
      }
    })
  } catch (error) {
    console.error('获取缓存统计失败:', error)
    throw error
  }
}

/**
 * 清空所有 IndexedDB 缓存
 */
export async function clearAllIndexedDBCache (flag = true) {
  try {
    const db: any = await initIndexedDB()

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite')
      const store = transaction.objectStore(STORE_NAME)

      const clearRequest = store.clear()

      clearRequest.onsuccess = () => {
        console.log('所有 IndexedDB 缓存已清空')
        if (flag) message.success('模型缓存已清空')
        resolve(true)
      }

      clearRequest.onerror = (event: Event) => {
        console.error('清空缓存失败:', (event.target as IDBRequest).error)
        if (flag) message.error('清空缓存失败')
        reject((event.target as IDBRequest).error)
      }

      transaction.oncomplete = () => {
        db.close()
      }
    })
  } catch (error) {
    console.error('清空缓存失败:', error)
    if (flag) message.error('清空缓存失败')
    throw error
  }
}
